import React, { useMemo } from 'react';
import { EFFECT_TYPE_LABELS_FI, normalizeEffectType } from '../../config/effectTypes';
import { nodeCardHeight, NODE_CARD_WIDTH } from './nodeCardMetrics';
import { loopColor } from './loopVisuals';

/**
 * LoopEdgeLayer — the closing edge, drawn the way it was drawn.
 *
 * A back-edge cannot be laid out: the tree has no column for it. So it is drawn
 * as what it is — a return arc that leaves the source card's handle, rises over
 * the map and comes down into the top edge of the box it points back at. It
 * touches nothing else's position (the anti-reflow rule), which is exactly the
 * freedom an overlay has and a spine edge does not.
 *
 * It carries what every other edge carries: the type pill saying how it acts,
 * because that polarity is what makes the loop reinforcing or balancing. Beside
 * the pill is the loop's own mark — R1, B2 — which is both the causal-loop
 * -diagram label and the way into the loop's description.
 */

/** Where the arrow lands on the target's top edge — a quarter of the way in
 *  from the side the arc arrives from, so it reads as landing on the box and
 *  does not come down on top of that box's own handle. */
const ENTRY_INSET = NODE_CARD_WIDTH * 0.25;

/** Corner radius on the orthogonal run. */
const CORNER = 18;

/**
 * Up out of the source, along the channel, down into the target: three straight
 * runs and two rounded corners. A curve looked like a decoration draped over
 * the map; a right-angled run reads as a route, which is what it is — and it
 * has a channel of its own to travel in, so it no longer crosses the cards it
 * passes.
 */
function orthogonalPath(sx, sy, tx, ty, channelY) {
  const dir = tx >= sx ? 1 : -1;
  const span = Math.abs(tx - sx);
  // Too narrow to fit two corners: fall back to a plain elbow.
  const r = Math.min(CORNER, span / 2, Math.abs(sy - channelY), Math.abs(ty - channelY));
  if (!(r > 1)) return `M ${sx} ${sy} L ${sx} ${channelY} L ${tx} ${channelY} L ${tx} ${ty}`;
  return [
    `M ${sx} ${sy}`,
    `L ${sx} ${channelY + r}`,
    `Q ${sx} ${channelY} ${sx + dir * r} ${channelY}`,
    `L ${tx - dir * r} ${channelY}`,
    `Q ${tx} ${channelY} ${tx} ${channelY + r}`,
    `L ${tx} ${ty}`,
  ].join(' ');
}

export default function LoopEdgeLayer({
  loops = [],
  connections = [],
  nodes = [],
  positions = {},
  // Per loop id: the y of the clear horizontal band the layout reserved for it.
  channels = {},
  onLoopClick,
  onTypeClick,
  selection = null,
  onSelect = null,
}) {
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const drawn = useMemo(() => {
    const counters = { reinforcing: 0, balancing: 0 };
    return loops
      .map((loop) => {
        const from = positions[loop.closingFromId];
        const to = positions[loop.closingToId];
        if (!from || !to) return null;

        const fromHeight = nodeCardHeight(nodeById.get(loop.closingFromId));
        const toHeight = nodeCardHeight(nodeById.get(loop.closingToId));

        // Leaves the source handle, lands on the target's top edge — the same
        // two points the drag used, so the arc reads as the gesture that made it.
        const sx = from.x;
        const sy = from.y - fromHeight / 2;
        const ty = to.y - toHeight / 2;
        const tx = to.x + (sx >= to.x ? ENTRY_INSET : -ENTRY_INSET);
        // The layout reserved a band for this loop; without one (a loop whose
        // nodes moved out from under it) stay just above the higher card.
        const channelY = channels[loop.id] ?? (Math.min(sy, ty) - 60);
        const path = orthogonalPath(sx, sy, tx, ty, channelY);
        // The pill and mark ride the horizontal run, where there is room.
        const midX = (sx + tx) / 2;
        const midY = channelY;

        const connection = connections.find(
          (c) => (c.parentId || c.parent_node_id) === loop.closingFromId
            && (c.childId || c.child_node_id) === loop.closingToId,
        );
        const type = normalizeEffectType(connection?.type || connection?.connectionType);
        const kind = loop.kind === 'balancing' ? 'balancing' : 'reinforcing';
        counters[kind] += 1;
        const color = loopColor(loop);

        return {
          loop,
          path,
          midX,
          midY,
          tx,
          ty,
          color,
          // The pill wears the loop's colour, not the type's.
          //
          // The argument for keeping the type's colour was that green means
          // "vahvistaa" everywhere on the map, and it holds for spine edges and
          // cross-links — but not here. A pill sitting on a loop's own arc,
          // between that loop's red arc and its red mark and the red borders of
          // the cards it runs through, is read as part of the ring; in the
          // link's green it was the one thing on the ring saying something else.
          // The word still names the type. The colour names what it is part of.
          typeColor: color,
          label: (type && EFFECT_TYPE_LABELS_FI[type]) || 'liittyy',
          mark: `${kind === 'balancing' ? 'B' : 'R'}${counters[kind]}`,
          selectKey: `${loop.closingFromId}->${loop.closingToId}`,
          ids: loop.nodeIds?.length ? loop.nodeIds : [loop.closingFromId, loop.closingToId],
        };
      })
      .filter(Boolean);
  }, [loops, connections, positions, nodeById]);

  if (!drawn.length) return null;

  return (
    <g className="loop-edge-layer">
      <defs>
        {drawn.map((d) => (
          <marker
            key={`marker-${d.loop.id}`}
            id={`loop-arrow-${d.loop.id}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={d.color} />
          </marker>
        ))}
      </defs>

      {drawn.map((d) => {
        const pillWidth = d.label.length * 8.2 + 26;
        return (
          <g
            key={d.loop.id}
            className="loop-edge"
            opacity={selection && selection.key !== d.selectKey ? 0.09 : 1}
            style={{ transition: 'opacity 0.18s ease' }}
          >
            {/* Tapping the ring lights the whole ring, not just its two ends —
                a loop is a claim about every node it runs through. */}
            {onSelect && (
              <path
                d={d.path}
                stroke="transparent"
                strokeWidth={18}
                fill="none"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect({
                    key: d.selectKey,
                    ids: new Set(d.ids),
                    from: d.loop.closingFromId,
                    to: d.loop.closingToId,
                    // A loop is read as a ring, so focusing one lays out the
                    // whole chain rather than only the link that closes it.
                    chain: d.loop.nodeIds?.length ? d.loop.nodeIds : [d.loop.closingToId, d.loop.closingFromId],
                    loop: d.loop,
                    label: d.label,
                    color: d.color,
                  });
                }}
              />
            )}
            <path
              d={d.path}
              fill="none"
              stroke={d.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              markerEnd={`url(#loop-arrow-${d.loop.id})`}
              style={{ pointerEvents: 'none' }}
            />

            {/* The type pill, on the arc, like every other edge carries — and
                clickable like every other one, because how a loop's closing
                link acts is what decides whether the ring reinforces or
                balances. Changing it here re-reads the whole loop. */}
            <g
              transform={`translate(${d.midX - 22}, ${d.midY})`}
              style={{ cursor: onTypeClick ? 'pointer' : 'default' }}
              onClick={(e) => {
                if (!onTypeClick) return;
                e.stopPropagation();
                const box = e.currentTarget.getBoundingClientRect();
                onTypeClick(d.loop, { x: box.left + box.width / 2, y: box.top + box.height / 2 });
              }}
            >
              <rect
                x={-pillWidth / 2}
                y={-13}
                width={pillWidth}
                height={26}
                rx={13}
                fill="#12121c"
                stroke={d.typeColor}
                strokeWidth={1.5}
              />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={13}
                fontWeight={600}
                fill={d.typeColor}
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {d.label}
              </text>
            </g>

            {/* The loop's mark, and the way into its words. */}
            <g
              className="loop-badge"
              transform={`translate(${d.midX + pillWidth / 2 - 8}, ${d.midY})`}
              style={{ cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); onLoopClick?.(d.loop); }}
            >
              <title>{d.loop.title}</title>
              <circle r={15} fill={d.color} />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fontWeight={800}
                fill="#12121c"
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {d.mark}
              </text>
              {d.loop.hasDelay && (
                /* The CLD delay mark: two strokes across the link. */
                <g stroke={d.color} strokeWidth={2.5} strokeLinecap="round">
                  <line x1={20} y1={-9} x2={26} y2={-15} />
                  <line x1={25} y1={-4} x2={31} y2={-10} />
                </g>
              )}
            </g>
          </g>
        );
      })}
    </g>
  );
}
